import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Leaf, Mail, MapPin, Phone } from "lucide-react";
import {
  FaFacebookF,
  FaPinterestP,
  FaTwitter,
  FaVimeoV,
} from "react-icons/fa";

const companyLinks = [
  "Home",
  "About Us",
  "Our Mission",
  "Help Center",
  "Contact Us",
];

const serviceLinks = [
  { label: "Landscaping Design & Build", href: "/landscaping-design-build" },
  { label: "Hydroseeding", href: "/hydroseeding" },
  { label: "Washing & Water Blasting", href: "/washing-water-blasting" },
  { label: "Property Maintenance", href: "/property-maintenance" },
  { label: "Lawn Maintenance", href: "/lawn-maintenance" },
  { label: "Fencing", href: "/fencing" },
];

const socialLinks = [
  { label: "Facebook", icon: FaFacebookF },
  { label: "Twitter", icon: FaTwitter },
  { label: "Vimeo", icon: FaVimeoV },
  { label: "Pinterest", icon: FaPinterestP },
];
const businessEmail = "Info@thelandscapingchristchurch.co.nz";
const businessAddress = "Christchurch, New Zealand";
const businessMapUrl = "https://maps.app.goo.gl/jpcSNMC3kdKK1b99A?g_st=iw";

const Footer = () => {
  return (
    <footer className="relative overflow-hidden bg-[#0b5a1d] font-sans text-white">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1515150144380-bca9f1650ed9?auto=format&fit=crop&w=1800&q=80')] bg-cover bg-center opacity-40" />
      <div className="absolute inset-0 bg-[#0b5a1d]/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a5a20]/95 via-[#0a5a20]/88 to-[#0a5a20]/95" />

      <div className="relative mx-auto max-w-[1428px] px-6 py-8 md:px-12">
        <div className="flex flex-col gap-7 border-b border-white/25 pb-[72px] lg:flex-row lg:items-start lg:justify-between">
          <a href="/" className="flex items-center gap-4">
            <span className="relative grid h-[56px] w-[56px] flex-shrink-0 place-items-center rounded-full border border-lime-300/45 bg-emerald-950/70 shadow-[0_0_0_7px_rgba(202,235,62,0.1)]">
              <span className="absolute bottom-3 left-3 h-5 w-9 rounded-t-full bg-emerald-500" />
              <span className="absolute right-3 top-3 h-6 w-6 rotate-45 rounded-sm bg-[#d5ea3d]" />
              <Leaf size={30} strokeWidth={2.1} className="relative z-10 text-white" />
            </span>
            <span className="leading-none">
              <span className="block text-[25px] font-black leading-none tracking-[-0.03em] md:text-[25px]">
                Landscaping
              </span>
              <span className="mt-2 block text-[14px] font-extrabold uppercase tracking-[0.32em] text-[#d5ea3d] md:text-[16px]">
                Christchurch
              </span>
            </span>
          </a>

          <div className="flex flex-col gap-1 pt-5 text-[16px] font-extrabold leading-none md:flex-row md:items-center md:gap-0">
            <span className="text-[#cbe43c]">Mon - Fri:</span>
            <span className="ml-1 text-white/90">9.00 am - 8.00pm</span>
            <span className="text-[#cbe43c] md:ml-1">Saturday:</span>
            <span className="ml-1 text-white/90">10.00 am - 8.00pm</span>
          </div>

          <div className="flex items-center gap-4 pt-1">
            {socialLinks.map((social) => {
              const Icon = social.icon;
              return (
                <motion.a
                  key={social.label}
                  href="#top"
                  aria-label={social.label}
                  whileHover={{ y: -3 }}
                  className="grid h-[52px] w-[52px] place-items-center rounded-full border border-white/75 text-white transition hover:border-[#cbe43c] hover:bg-[#cbe43c] hover:text-[#0b5a1d]"
                >
                  <Icon size={18} />
                </motion.a>
              );
            })}
          </div>
        </div>

        <div className="grid gap-12 pb-1 pt-[70px] lg:grid-cols-[1.28fr_0.72fr_0.94fr_1fr]">
          <div className="lg:pt-0">
            <h2 className="max-w-[360px] text-[32px] font-black leading-[1.28] tracking-[-0.02em] md:text-[34px]">
              Ready to Transform Your Outdoor Space?
            </h2>
            <a
              href="#contact"
              className="mt-9 inline-flex h-[66px] min-w-[232px] items-center justify-center gap-2 rounded-[10px] bg-[#d5ea3d] px-8 text-[15px] font-black uppercase text-black transition hover:bg-[#e1f24e]"
            >
              Request a Quote
              <ArrowRight size={22} strokeWidth={2.4} />
            </a>

            <div className="mt-9">
              <p className="text-[16px] font-black text-[#d5ea3d]">Call Us</p>
              <a href="tel:+64228980029" className="mt-4 flex items-center gap-3 text-[19px] font-black">
                <Phone size={21} />
                +64 228 980 029
              </a>
            </div>
          </div>

          <div>
            <h3 className="text-[21px] font-black leading-none">Company</h3>
            <ul className="mt-10 space-y-[25px]">
              {companyLinks.map((link) => (
                <li key={link}>
                  <a href={link === "Home" ? "/" : link === "About Us" ? "/about" : link === "Contact Us" ? "/contact" : "#top"} className="text-[17px] font-normal leading-none text-white/80 transition hover:text-[#d5ea3d]">
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[21px] font-black leading-none">Services</h3>
            <ul className="mt-10 space-y-[25px]">
              {serviceLinks.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-[17px] font-normal leading-none text-white/80 transition hover:text-[#d5ea3d]">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-[21px] font-black leading-none">Address</h3>
            <div className="mt-10 space-y-[22px] text-[17px] font-normal leading-[1.75] text-white/80">
              <a href={businessMapUrl} target="_blank" rel="noreferrer" className="flex gap-4 transition hover:text-[#d5ea3d]">
                <MapPin size={23} className="mt-1 flex-shrink-0 text-[#d5ea3d]" />
                <span>{businessAddress}</span>
              </a>
              <a href={`mailto:${businessEmail}`} className="flex gap-4 transition hover:text-[#d5ea3d]">
                <Mail size={23} className="mt-1 flex-shrink-0 text-[#d5ea3d]" />
                <span>{businessEmail}</span>
              </a>
              <a href="tel:+64228980029" className="flex gap-4 transition hover:text-[#d5ea3d]">
                <Phone size={23} className="mt-1 flex-shrink-0 text-[#d5ea3d]" />
                <span>+64 228 980 029</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
